import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { ActionBar, Button, type Column } from "@fawkes/ui";
import qrcode from "qrcode-generator";

import { formatDateTime } from "../../i18n/format";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/**
 * "Mijn apparaten" — de telefoons en tablets waarop deze medewerker de dierverzorgers-app gebruikt, en
 * de koppelcode waarmee er eentje bijkomt.
 *
 * <p><b>De QR en de tekst staan naast elkaar</b> (2026-08-21). Scannen is de snelle weg, maar de code
 * staat er ook uitgeschreven bij — zie hieronder. Beide dragen dezelfde code: de server stelt
 * {@code qrPayload} samen, dit scherm tekent hem alleen.
 *
 * <p><b>De code staat er als tekst</b> (2026-08-21). De app heeft een handmatige koppelweg voor wie
 * niet kan scannen: een camera die stuk is, een tablet aan de muur, een toestel dat de QR niet scherp
 * krijgt. Die weg valt of staat met een code die van het scherm te lezen is — daarom staan het adres en
 * de code hier gewoon uitgeschreven, groot genoeg om over te tikken, met een knop om ze te kopiëren.
 * De code zelf is daarop gemaakt: zes tekens in twee groepjes, zonder tekens die op elkaar lijken.
 *
 * <p><b>Het adres komt van de server</b> en wordt niet hier verzonnen. Wijst het naar de machine zelf
 * ({@code localhost}), dan zegt de server dat erbij en waarschuwt dit scherm: voor een telefoon is
 * {@code localhost} de telefoon zelf, dus zo'n code werkt op geen enkel toestel.
 *
 * <p>De code leeft een paar minuten en werkt één keer. De teller loopt daarom zichtbaar af, en als hij
 * op is verdwijnt de code van het scherm in plaats van er verlopen te blijven staan — anders staat
 * iemand met zijn telefoon een code over te tikken die het al niet meer doet.
 */
interface Apparaat {
  id: string;
  name: string;
  platform: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface Koppelcode {
  token: string;
  expiresAt: string;
  expiresInSeconds: number;
  organisatie: string;
  baseUrl: string;
  qrPayload: string;
  /**
   * Zegt de server dat het adres van buitenaf te bereiken is. Optioneel, en dat is met opzet: een
   * api die dit veld nog niet kent stuurt niets mee, en dan is er geen reden om te waarschuwen.
   * Alleen een uitdrukkelijke {@code false} is een waarschuwing waard.
   */
  baseUrlBruikbaar?: boolean;
}

export function MijnApparatenTab() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, refetch } = useCustom<Apparaat[]>({ url: "me/devices", method: "get" });
  const { mutate: roep, isLoading: bezig } = useCustomMutation();
  const [code, setCode] = useState<Koppelcode | null>(null);
  const [resterend, setResterend] = useState(0);
  const [fout, setFout] = useState<string | null>(null);
  const [gekopieerd, setGekopieerd] = useState<string | null>(null);
  const [zojuistGekoppeld, setZojuistGekoppeld] = useState<string | null>(null);
  /** De apparaten die er al waren toen de code werd aangemaakt; wat daarna verschijnt is deze koppeling. */
  const bekendeIds = useRef<Set<string>>(new Set());

  const apparaten = data?.data ?? [];
  const apparatenRef = useRef(apparaten);
  apparatenRef.current = apparaten;
  const idsSleutel = apparaten.map((a) => a.id).join(",");

  // De teller loopt op de klok van deze browser, maar begint bij wat de server zegt: de server bepaalt
  // hoe lang de code geldig is, niet wij.
  useEffect(() => {
    if (!code) return;
    const tik = () => {
      const over = Math.round((new Date(code.expiresAt).getTime() - Date.now()) / 1000);
      setResterend(over > 0 ? over : 0);
      if (over <= 0) setCode(null);
    };
    tik();
    const timer = globalThis.setInterval(tik, 1000);
    return () => globalThis.clearInterval(timer);
  }, [code]);

  // Wie hier een code aanmaakt staat met de telefoon in de hand te kijken. Zonder dit blijft het scherm
  // een code aftellen die al gebruikt is, en blijft eronder staan dat er geen apparaat gekoppeld is —
  // terwijl het toestel er allang in staat. De server kan niets naar de browser duwen, dus vragen we het
   // zelf, en alleen zolang er een code openstaat: daarbuiten is er niets te verwachten.
  useEffect(() => {
    if (!code) return;
    const timer = globalThis.setInterval(() => void refetch(), 3000);
    return () => globalThis.clearInterval(timer);
  }, [code, refetch]);

  useEffect(() => {
    if (!code) return;
    const nieuw = apparatenRef.current.find((a) => !bekendeIds.current.has(a.id));
    if (!nieuw) return;
    // De code is op: hem laten staan nodigt uit tot een tweede poging die nooit kan lukken.
    setCode(null);
    setZojuistGekoppeld(nieuw.name);
  }, [idsSleutel, code]);

  // De QR is de snelle weg; de tekst eronder is de weg die altijd werkt. Beide dragen precies dezelfde
  // code, want de server stelt qrPayload samen en wij verzinnen er niets bij.
  const qrSvg = useMemo(() => {
    if (!code?.qrPayload) return null;
    const qr = qrcode(0, "M");
    // De generator leest één byte per teken; de payload eerst naar UTF-8-bytes brengen, anders loopt
    // een opvangnaam met een accent erin stuk.
    qr.addData(String.fromCharCode(...new TextEncoder().encode(code.qrPayload)), "Byte");
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [code?.qrPayload]);

  const maakCode = useCallback(() => {
    setFout(null);
    setGekopieerd(null);
    roep(
      { url: "me/devices/pairing-token", method: "post", values: {} },
      {
        onSuccess: (res) => {
          bekendeIds.current = new Set(apparatenRef.current.map((a) => a.id));
          setZojuistGekoppeld(null);
          setCode(res.data as unknown as Koppelcode);
        },
        onError: () => setFout(t("voorkeuren.apparaten.codeMislukt")),
      },
    );
  }, [roep, t]);

  function kopieer(wat: string, welke: string) {
    // Zonder clipboard-rechten (of in een test) blijft de tekst gewoon op het scherm staan om te lezen;
    // dat is de bedoeling van dit scherm, kopiëren is het extraatje.
    void navigator.clipboard?.writeText(wat).then(
      () => setGekopieerd(welke),
      () => setGekopieerd(null),
    );
  }

  function trekIn(apparaat: Apparaat) {
    roep(
      { url: `me/devices/${apparaat.id}`, method: "delete", values: {} },
      { onSuccess: () => void refetch(), onError: () => setFout(t("voorkeuren.apparaten.intrekkenMislukt")) },
    );
  }

  const kolommen: Column<Apparaat>[] = [
    { header: t("voorkeuren.apparaten.kolom.naam"), cell: (a) => a.name },
    { header: t("voorkeuren.apparaten.kolom.platform"), cell: (a) => a.platform ?? "—" },
    {
      header: t("voorkeuren.apparaten.kolom.gekoppeld"),
      cell: (a) => formatDateTime(a.createdAt, locale),
    },
    {
      header: t("voorkeuren.apparaten.kolom.laatstGebruikt"),
      cell: (a) => (a.lastUsedAt ? formatDateTime(a.lastUsedAt, locale) : t("voorkeuren.apparaten.nooitGebruikt")),
    },
    {
      header: "",
      align: "right",
      cell: (a) => (
        <Button variant="secondary" onClick={() => trekIn(a)} disabled={bezig}>
          {t("voorkeuren.apparaten.intrekken")}
        </Button>
      ),
    },
  ];

  return (
    <div className="voorkeuren-tab">
      <p className="fawkes-muted">{t("voorkeuren.apparaten.uitleg")}</p>

      <ActionBar>
        <Button variant="primary" onClick={maakCode} disabled={bezig}>
          {t("voorkeuren.apparaten.codeMaken")}
        </Button>
      </ActionBar>

      {fout && <p className="fawkes-error" role="alert">{fout}</p>}

      {code && (
        <div className="voorkeuren-koppelcode">
          {code.baseUrlBruikbaar === false && (
            <p className="fawkes-error" role="alert">{t("voorkeuren.apparaten.adresOnbruikbaar")}</p>
          )}

          <div className="voorkeuren-koppelcode__inhoud">
            {qrSvg && (
              <div
                className="voorkeuren-koppelcode__qr"
                role="img"
                aria-label={t("voorkeuren.apparaten.qrAlt")}
                // De SVG komt uit de generator hiernaast, niet van de server: die krijgt alleen de
                // tekst uit qrPayload te verwerken en zet er zelf geen opmaak in.
                dangerouslySetInnerHTML={{ __html: qrSvg }}
              />
            )}

            <dl className="voorkeuren-koppelcode__regels">
              <dt>{t("voorkeuren.apparaten.adres")}</dt>
              <dd>
                <code className="voorkeuren-koppelcode__adres">{code.baseUrl}</code>
                <Button variant="secondary" onClick={() => kopieer(code.baseUrl, "adres")}>
                  {t("voorkeuren.apparaten.kopieer")}
                </Button>
              </dd>

              <dt>{t("voorkeuren.apparaten.code")}</dt>
              <dd>
                <code className="voorkeuren-koppelcode__code">{code.token}</code>
                <Button variant="secondary" onClick={() => kopieer(code.token, "code")}>
                  {t("voorkeuren.apparaten.kopieer")}
                </Button>
              </dd>

              <dt>{t("voorkeuren.apparaten.opvang")}</dt>
              <dd>{code.organisatie}</dd>
            </dl>
          </div>

          {gekopieerd && (
            <p className="fawkes-muted" role="status">{t("voorkeuren.apparaten.gekopieerd")}</p>
          )}

          <p className="fawkes-muted" role="status">
            {t("voorkeuren.apparaten.geldigNog", { args: [klok(resterend)] })}
          </p>
          <p className="fawkes-muted">{t("voorkeuren.apparaten.handmatig")}</p>
          <p className="fawkes-muted" role="status">{t("voorkeuren.apparaten.wachtOpToestel")}</p>
        </div>
      )}

      {zojuistGekoppeld && (
        <p className="fawkes-success" role="status">
          {t("voorkeuren.apparaten.zojuistGekoppeld", { args: [zojuistGekoppeld] })}
        </p>
      )}

      <h3 className="voorkeuren-koppelcode__kop">{t("voorkeuren.apparaten.gekoppeld")}</h3>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : apparaten.length === 0 ? (
        <p className="fawkes-muted">{t("voorkeuren.apparaten.geen")}</p>
      ) : (
        <IndeelbareTabel tabelKey="voorkeuren-mijn-apparaten-tab" columns={kolommen} rows={apparaten} getRowKey={(a) => a.id} />
      )}
    </div>
  );
}

/** Mm:ss, want "nog 214 seconden" leest niemand als "nog even". */
function klok(seconden: number): string {
  const m = Math.floor(seconden / 60);
  const s = seconden % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
